import { ImportData } from '../tree/import-data';
import { PublishData } from './publish-data';
import { Prototype } from '../tree/prototype';
import { Secure } from '../base/secure';
import { Branch } from '../base/branch';
import { Site } from './site';
import { Content } from './content';
import { Preview } from './preview';
import { PreviewItem } from './preview-item';

export class Genus extends Secure {
    static _type: string = 'Genus';

    i_genus: true = true;

    prototypeId?: string;
    prototype?: Prototype;
    import?: ImportData;
    publish?: PublishData;
    graphqlType?: string;
    siteId?: string;

    aspects?: string[];
    attributes?: string[];

    data?: any;
    preview?: Preview;

    get contentPreviews(): PreviewItem[] {
        const result: PreviewItem[] = [...(this.preview?.content ?? [])];
        const parent = this.parent as Genus;

        if (parent?.contentPreviews?.length) {
            result.push(...parent.contentPreviews);
        }

        return result;
    }

    get genusPreviews(): PreviewItem[] {
        const result: PreviewItem[] = [...(this.preview?.genus ?? [])];
        const parent = this.parent as Genus;

        if (parent?.genusPreviews?.length) {
            result.push(...parent.genusPreviews);
        }

        return result;
    }

    constructor(source?: any) {
        super(source);

        if (source) {
            const { parent, nodes, publish, aspects, attributes, data, preview, ...rest } = source;

            this.prototypeId = rest.prototypeId;
            this.graphqlType = rest.graphqlType;
            this.siteId = rest.siteId;
            this.aspects = aspects;
            this.attributes = attributes;
            this.data = data;

            if (preview) {
                this.preview = new Preview(preview);
            }

            if (publish) {
                this.publish = new PublishData(publish);
            }

            if (rest.import) {
                this.import = new ImportData(rest.import);
            }
        }
    }

    override copy(source: any): void {
        super.copy(source);

        if (source) {
            const { parent, nodes, publish, aspects, attributes, preview, ...rest } = source;

            this.prototypeId = rest.prototypeId;
            this.graphqlType = rest.graphqlType;
            this.siteId = rest.siteId;
            this.aspects = aspects;
            this.attributes = attributes;
            this.data = rest.data;

            if (preview) {
                this.preview = new Preview(preview);
            }

            if (publish) {
                this.publish = new PublishData(publish);
            }

            if (rest.import) {
                this.import = new ImportData(rest.import);
            }
        }
    }

    get site(): Site | undefined {
        if (this.parent instanceof Site) {
            return this.parent;
        } else if (this.parent instanceof Genus) {
            return this.parent.site;
        } else {
            return undefined;
        }
    }

    get model(): any {
        const { prototype, ...rest } = super.model;
        if (rest.import) {
            rest.import.jsonResponse = undefined;
            rest.import.jsonImport = undefined;
        }
        return rest;
    }

    override load(source?: any): void {
        this.copy(source);

        if (source) {
            const { nodes, branches } = source;

            nodes?.forEach((node: any) => {
                const content = new Content(node);
                this.nodes.push(content);
            });

            branches?.forEach((branch: any) => {
                if (branch?.i_site) {
                    const site = new Site(branch);
                    this.branches.push(site);
                } else if (branch?.i_folder) {
                    const folder = new Folder(branch);
                    this.branches.push(folder);
                }
            });
        }
    }

    override fit(branch: Branch): boolean {
        return branch instanceof Folder && branch.siteId == this.siteId && !branch.prototypeId
        || branch instanceof Site && this.siteId === branch._id;
    }

    fitParent(folder: Folder): boolean {
        const inheritAspects = folder.inheritAspects ?? [];
        const aspects = this.aspects ?? [];
        return this.prototypeId === folder.prototypeId && this.siteId === folder.siteId
        && inheritAspects.length === aspects.length
        && inheritAspects.every(aspect => aspects.indexOf(aspect) >= 0);
    }
}

export class Folder extends Genus {
    static _type: string = 'Folder';

    public i_folder: true = true;

    public inheritAspects?: string[];
    public inheritAttributes?: string[];

    get model(): any {
        const { inheritAspects, inheritAttributes, ...rest } = super.model;
        return rest;
    }

    constructor(source?: any) {
        super(source);

        if (source) {
            this.inheritAspects = source.inheritAspects;
            this.inheritAttributes = source.inheritAttributes;
        }
    }

    override copy(source: any): void {
        super.copy(source);

        if (source) {
            this.inheritAspects = source.inheritAspects;
            this.inheritAttributes = source.inheritAttributes;
        }
    }

    override load(source?: any): void {
        this.copy(source);

        if (source) {
            const { nodes, branches } = source;

            nodes?.forEach((node: any) => {
                const content = new Content(node);
                this.nodes.push(content);
            });

            branches?.forEach((branch: any) => {
                if (branch?.i_folder) {
                    const folder = new Folder(branch);
                    this.branches.push(folder);
                } else if (branch?.i_genus) {
                    const genus = new Genus(branch);
                    this.branches.push(genus);
                }
            });
        }
    }

    override fit(branch: Branch): boolean {
        return branch instanceof Folder && this.fitFolder(branch)
            || branch instanceof Genus && !(branch instanceof Folder) && this.fitGenus(branch)
            || branch instanceof Site && this.fitSite(branch);
    }

    fitParent(genus: Genus): boolean {
        if (genus instanceof Folder) {
            const folder: Folder = genus;
            if (this.prototypeId === folder.prototypeId && this.siteId === folder.siteId) {
                const aspectsThis = (this.inheritAspects ?? []).concat(this.aspects ?? []);
                const aspectsThat = (folder.inheritAspects ?? []).concat(folder.aspects ?? []);
                return aspectsThis.length === aspectsThat.length
                    && aspectsThis.every(aspect => aspectsThat.indexOf(aspect) >= 0);
            }
        } else {
            const inheritAspects = this.inheritAspects ?? [];
            const genusAspects = genus.aspects ?? [];
            return this.prototypeId === genus.prototypeId && this.siteId === genus.siteId
                && inheritAspects.length === genusAspects.length
                && inheritAspects.every(aspect => genusAspects?.indexOf(aspect) >= 0);
        }

        return false;
    }

    private fitGenus(genus: Genus): boolean {
        if (this.prototypeId === genus.prototypeId && this.siteId === genus.siteId && this.parentId !== genus._id && !(genus instanceof Folder)) {
            const aspectsThis = this.inheritAspects ?? [];
            const aspectsThat = genus.aspects ?? [];

            return aspectsThis.length === aspectsThat.length
                && aspectsThis.every(aspect => aspectsThat.indexOf(aspect) >= 0);
        }

        return false;
    }

    private fitFolder(folder: Folder): boolean {
        if (!this.prototypeId && !folder.prototypeId || this.prototypeId === folder.prototypeId && this.siteId === folder.siteId && this.parentId != folder._id) {
            const aspectsThis = (this.inheritAspects ?? []).concat(this.aspects ?? []);
            const aspectsThat = (folder.inheritAspects ?? []).concat(folder.aspects ?? []);
            return aspectsThis.length === aspectsThat.length
                && aspectsThis.every(aspect => aspectsThat.indexOf(aspect) >= 0);
        }

        return false;
    }

    private fitSite(site: Site): boolean {
        return site instanceof Site && this.siteId === site._id && !this.prototypeId?.length
    }
}
